import { useState, useEffect, useRef, useCallback } from 'react'
import { sessions } from '../api/client'
import { useSessionMode } from '../professional/SessionModeContext'

interface UseGameSessionOptions {
  activitySlug: string
  difficulty:   'easy' | 'medium' | 'hard'
  mode?:        string
  /** Only start session when true (e.g. screen === 'playing') */
  enabled:      boolean
}

/**
 * Manages a server-side game session lifecycle.
 *
 * - Calls `POST /sessions/start` on mount when `enabled` is true.
 * - Returns a `sessionId` and a `completeSession` function.
 * - Skips entirely when in professional session mode (playbook handles its own sessions).
 */
export function useGameSession({
  activitySlug,
  difficulty,
  mode,
  enabled,
}: UseGameSessionOptions): {
  sessionId:        string | null
  completeSession:  (stats: { durationMs?: number; moves?: number; pairsTotal?: number; pairsMatched?: number; themeId?: number }) => Promise<void>
} {
  const sessionMode = useSessionMode()
  const [sessionId, setSessionId] = useState<string | null>(null)

  // Prevent double-start if options change while enabled is still true
  const startedRef = useRef(false)
  const sessionIdRef = useRef<string | null>(null)

  useEffect(() => {
    if (!enabled || sessionMode) {
      startedRef.current = false
      return
    }
    if (startedRef.current) return

    startedRef.current = true
    sessions.start(activitySlug, difficulty, mode)
      .then(({ sessionId: id }) => {
        sessionIdRef.current = id
        setSessionId(id)
      })
      .catch((err) => {
        console.error('[useGameSession] Failed to start session:', err)
      })
  }, [enabled, sessionMode, activitySlug, difficulty, mode])

  const completeSession = useCallback(async (stats: {
    durationMs?: number
    moves?: number
    pairsTotal?: number
    pairsMatched?: number
    themeId?: number
  }) => {
    const id = sessionIdRef.current
    if (!id) return
    try {
      await sessions.complete({ sessionId: id, ...stats })
    } catch (err) {
      console.error('[useGameSession] Failed to complete session:', err)
      throw err
    }
  }, [])

  return { sessionId, completeSession }
}
